import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Empty, EmptyDescription, EmptyIcon, EmptyTitle } from '../components/ui/empty';
import { Skeleton } from '../components/ui/skeleton';
// 修改导入方式，使用 api 实例
import api, { ENDPOINTS } from '../config/api';
import { toast } from 'react-hot-toast';
import { 
  Settings2, 
  FileText, 
  ArrowRight, 
  CheckCircle2,
  X,
  XCircle, // 添加 XCircle
  Award,
  Key,
  Server,
  Users,
  Calendar,
  TrendingUp,
  HelpCircle,
  User,
  Loader2,
  AlertCircle,
  Activity,
  Cpu,
  Layers,
  MemoryStick,
  Eye as EyeIcon,
  Trash2, // 添加 Trash2 图标用于删除活动
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

const ACTIVITY_PAGE_SIZE = 5;

const FeatureCard = ({ index, title, description, icon: Icon, onClick, buttonText }) => {
  return (
    <button type="button" className="desk-route" onClick={onClick}>
      <span className="desk-route__index">{String(index + 1).padStart(2, '0')}</span>
      <span className="desk-route__icon" aria-hidden="true"><Icon /></span>
      <span className="desk-route__copy">
        <strong>{title}</strong>
        <small>{description}</small>
      </span>
      <span className="desk-route__action">
        {buttonText}<ArrowRight aria-hidden="true" />
      </span>
    </button>
  );
};

FeatureCard.propTypes = {
  index: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  icon: PropTypes.elementType.isRequired,
  onClick: PropTypes.func.isRequired,
  buttonText: PropTypes.string.isRequired,
};

const formatActivityTimestamp = (value) => {
  const timestamp = new Date(value);

  if (Number.isNaN(timestamp.getTime())) {
    const [datePart = value, timePart = ''] = String(value).split(/[,，\s]+/, 2);
    return { datePart, timePart };
  }

  return {
    datePart: timestamp.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }),
    timePart: timestamp.toLocaleTimeString('zh-CN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }),
  };
};

// 新增的活动项目组件
const ActivityItem = ({ date, title, description, icon: Icon }) => {
  const { datePart, timePart } = formatActivityTimestamp(date);

  return (
    <div className="ledger-entry">
      <time dateTime={date}>
        <span>{datePart}</span>
        {timePart && <strong>{timePart}</strong>}
      </time>
      <span className="ledger-entry__marker" aria-hidden="true"><Icon /></span>
      <div className="ledger-entry__copy">
        <h4>{title}</h4>
        <p>{description}</p>
      </div>
    </div>
  );
};

ActivityItem.propTypes = {
  date: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  icon: PropTypes.elementType.isRequired,
};

// 活动骨架屏
const ActivitySkeleton = () => (
  <div className="ledger-entry ledger-entry--skeleton">
    <Skeleton className="h-3 w-20" />
    <Skeleton className="size-8" />
    <div className="flex min-w-0 flex-1 flex-col gap-2">
      <Skeleton className="h-3 w-2/5" />
      <Skeleton className="h-2 w-4/5" />
    </div>
  </div>
);

const formatProcessCPU = (process) => {
  if (process?.cpu_available === false) return '-';

  const rawValue = process?.cpu_percent ?? process?.cpu;
  if (typeof rawValue === 'string' && rawValue.trim() && rawValue.includes('%')) {
    return rawValue;
  }

  const value = Number(rawValue);
  return Number.isFinite(value) ? `${value.toFixed(1)}%` : '-';
};

const formatProcessMemory = (process) => {
  if (process?.memory_available === false && process?.memory == null) return '-';

  const memoryMB = Number(process?.memory_mb);
  const memoryPercent = Number(process?.memory_percentage);
  if (Number.isFinite(memoryMB)) {
    const size = memoryMB > 0 && memoryMB < 0.1 ? '<0.1 MB' : `${memoryMB.toFixed(1)} MB`;
    return Number.isFinite(memoryPercent) ? `${size} · ${memoryPercent.toFixed(1)}%` : size;
  }

  return process?.memory ?? '-';
};

const formatSummaryMetric = (value) => {
  if (value === null || value === undefined || value === '') return '不可用';
  return String(value);
};

const getMetricPercentage = (value) => {
  const numericValue = Number.parseFloat(String(value ?? '').replace('%', ''));
  if (!Number.isFinite(numericValue)) return null;
  return Math.min(100, Math.max(0, numericValue));
};

const OVERVIEW_CACHE_TTL = 30 * 1000;
const overviewCache = new Map();

const readOverviewCache = (cacheKey) => overviewCache.get(cacheKey) || null;

const updateOverviewCache = (cacheKey, patch) => {
  overviewCache.set(cacheKey, {
    ...readOverviewCache(cacheKey),
    ...patch,
  });
};

const isOverviewCacheFresh = (timestamp) => (
  Number.isFinite(timestamp) && Date.now() - timestamp < OVERVIEW_CACHE_TTL
);

// 首页主组件
const Home = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.isAdmin;
  const overviewCacheKey = `${user?.id ?? user?.username ?? 'authenticated'}:${isAdmin ? 'admin' : 'member'}`;
  const initialOverviewCache = readOverviewCache(overviewCacheKey);
  
  // 添加状态来存储实际数据
  const [dashboardData, setDashboardData] = useState(() => initialOverviewCache?.dashboardData || ({
    totalLicenses: 0,
    monthlyLicenses: 0,
    serverCount: 0,
    userCount: 0,
    serverProgramsBreakdown: {
      atlassianPrograms: 0,
      webServers: 0,
      databases: 0,
      developerTools: 0,
      otherPrograms: 0
    },
    runningPrograms: {
      java: false,
      node: false, 
      nginx: false,
      mysql: false,
      tomcat: false
    }
  }));
  
  const [recentActivities, setRecentActivities] = useState(() => initialOverviewCache?.recentActivities || []);
  const [isLoading, setIsLoading] = useState(() => !initialOverviewCache?.dashboardData);
  const [activityLoading, setActivityLoading] = useState(() => !initialOverviewCache?.recentActivities);
  const [activityPage, setActivityPage] = useState(1);
  const [systemInfo, setSystemInfo] = useState(() => initialOverviewCache?.systemInfo || ({
    version: 'v1.0.0',
    status: '正常',
    lastUpdated: new Date().toLocaleString(),
    processCount: 0  // 添加进程数量
  }));

  const [showServerInfo, setShowServerInfo] = useState(false);
  const [serverInfo, setServerInfo] = useState(null);
  const [serverInfoError, setServerInfoError] = useState('');
  const [serverInfoLoading, setServerInfoLoading] = useState(false);
  const [selectedProcessCategory, setSelectedProcessCategory] = useState(null);

  const activityPageCount = Math.max(1, Math.ceil(recentActivities.length / ACTIVITY_PAGE_SIZE));
  const activityPageStart = (activityPage - 1) * ACTIVITY_PAGE_SIZE;
  const activityPageEnd = Math.min(activityPageStart + ACTIVITY_PAGE_SIZE, recentActivities.length);
  const visibleActivities = recentActivities.slice(activityPageStart, activityPageEnd);

  useEffect(() => {
    setActivityPage(1);
  }, [overviewCacheKey]);

  useEffect(() => {
    setActivityPage((currentPage) => Math.min(currentPage, activityPageCount));
  }, [activityPageCount]);

  useEffect(() => {
    if (!showServerInfo) return undefined;

    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setShowServerInfo(false);
    };

    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showServerInfo]);

  // 获取仪表盘数据
  useEffect(() => {
    let cancelled = false;
    const cachedOverview = readOverviewCache(overviewCacheKey);

    if (cachedOverview?.dashboardData) {
      setDashboardData(cachedOverview.dashboardData);
      setIsLoading(false);
    }

    if (isOverviewCacheFresh(cachedOverview?.dashboardFetchedAt)) {
      return () => {
        cancelled = true;
      };
    }

    const fetchDashboardData = async () => {
      const cachedDashboard = readOverviewCache(overviewCacheKey)?.dashboardData;

      try {
        if (!cachedDashboard) setIsLoading(true);
        
        // console.log('开始获取仪表盘数据...');
        
        // 使用更健壮的错误处理，避免一个请求失败导致整个数据加载失败
        let licenseStatsResponse = {
          totalLicenses: cachedDashboard?.totalLicenses ?? 0,
          monthlyGenerated: cachedDashboard?.monthlyLicenses ?? 0,
        };
        let serverDataResponse = {
          count: cachedDashboard?.serverCount ?? 0,
          breakdown: cachedDashboard?.serverProgramsBreakdown || {},
          running: cachedDashboard?.runningPrograms || {},
        };
        let userDataResponse = { count: cachedDashboard?.userCount ?? 0 };
        
        // 同时发起所有请求，提高加载速度
        const promises = [];
        
        // 许可证统计请求
        const licensePromise = api.get(ENDPOINTS.DASHBOARD_STATS)
          .then(result => {
            licenseStatsResponse = result.data;
            // console.log('许可证统计数据响应:', licenseStatsResponse);
          })
          .catch(() => {
            // 不显示toast，减少用户干扰
          });
        promises.push(licensePromise);
        
        // 服务器程序统计请求
        const serverPromise = api.get(ENDPOINTS.SERVER_COUNT_MODERN) // 使用现代化API
          .then(result => {
            serverDataResponse = result.data;
            // console.log('服务器程序数据响应:', serverDataResponse);
          })
          .catch(error => {
            console.warn('现代化API失败，回退到传统API:', error);
            // 如果现代化API失败，回退到传统API
            return api.get(ENDPOINTS.SERVER_COUNT)
              .then(result => {
                serverDataResponse = result.data;
              })
              .catch(fallbackError => {
                console.error('传统API也失败:', fallbackError);
              });
          });
        promises.push(serverPromise);
        
        // 用户数量统计请求（仅管理员）
        if (isAdmin) {
          const userPromise = api.get(ENDPOINTS.USER_COUNT)
            .then(result => {
              userDataResponse = result.data;
              // console.log('用户数量响应:', userDataResponse);
            })
            .catch(() => {
              // 不显示toast，减少用户干扰
            });
          promises.push(userPromise);
        }
        
        // 等待所有请求完成
        await Promise.allSettled(promises);

        if (cancelled) return;
        
        // 更新状态，使用默认值作为后备
        const nextDashboardData = {
          totalLicenses: licenseStatsResponse.totalLicenses || 0,
          monthlyLicenses: licenseStatsResponse.monthlyGenerated || 0,
          serverCount: serverDataResponse.count || 0,
          userCount: isAdmin ? (userDataResponse.count || 0) : 0,
          serverProgramsBreakdown: {
            applications: serverDataResponse.breakdown?.applications || 0,
            javaApps: serverDataResponse.breakdown?.javaApps || 0,
            webServers: serverDataResponse.breakdown?.webServers || 0,
            databases: serverDataResponse.breakdown?.databases || 0,
            systemProcs: serverDataResponse.breakdown?.systemProcs || 0,
            otherProcs: serverDataResponse.breakdown?.otherProcs || 0,
            // 保持向后兼容的旧字段名
            atlassianPrograms: serverDataResponse.breakdown?.javaApps || 0,
            developerTools: serverDataResponse.breakdown?.applications || 0,
            otherPrograms: serverDataResponse.breakdown?.otherProcs || 0
          },
          runningPrograms: serverDataResponse.running || {
            java: false,
            node: false,
            nginx: false,
            mysql: false,
            tomcat: false
          }
        };

        setDashboardData(nextDashboardData);
        updateOverviewCache(overviewCacheKey, {
          dashboardData: nextDashboardData,
          dashboardFetchedAt: Date.now(),
        });
      } catch (error) {
        if (cancelled) return;

        // console.error('获取仪表盘整体数据失败:', error);
        // 仅在整个过程失败时显示toast
        toast.error('获取数据失败: ' + (error.message || '未知错误'));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    
    fetchDashboardData();

    return () => {
      cancelled = true;
    };
  }, [isAdmin, overviewCacheKey]);
  
  // 获取最近活动
  useEffect(() => {
    let cancelled = false;
    const cachedOverview = readOverviewCache(overviewCacheKey);
    const hasCachedActivities = Array.isArray(cachedOverview?.recentActivities);

    if (hasCachedActivities) {
      setRecentActivities(cachedOverview.recentActivities);
      setActivityLoading(false);
    }

    if (isOverviewCacheFresh(cachedOverview?.activitiesFetchedAt)) {
      return () => {
        cancelled = true;
      };
    }

    const fetchRecentActivities = async () => {
      try {
        if (!hasCachedActivities) setActivityLoading(true);
        // console.log('开始获取最近活动数据...');
        
        // 使用 api 实例替代 fetchAPI
        const response = await api.get(ENDPOINTS.RECENT_ACTIVITIES);
        const activitiesResponse = response.data;
        // console.log('最近活动响应:', activitiesResponse);

        if (cancelled) return;
        
        if (Array.isArray(activitiesResponse) && activitiesResponse.length > 0) {
          const nextActivities = activitiesResponse.map(activity => ({
            date: new Date(activity.created_at).toLocaleString(),
            title: activity.action,
            description: activity.description,
            icon: determineActivityIcon(activity.type)
          }));

          setRecentActivities(nextActivities);
          updateOverviewCache(overviewCacheKey, {
            recentActivities: nextActivities,
            activitiesFetchedAt: Date.now(),
          });
        } else {
          // console.warn('未获取到活动数据或数据为空数组');
          // 使用默认活动数据
          const nextActivities = [
            {
              date: "今天 " + new Date().toLocaleTimeString('zh-CN', {hour: '2-digit', minute:'2-digit'}),
              title: "无最近活动",
              description: "系统暂无记录的活动",
              icon: Calendar
            }
          ];

          setRecentActivities(nextActivities);
          updateOverviewCache(overviewCacheKey, {
            recentActivities: nextActivities,
            activitiesFetchedAt: Date.now(),
          });
        }
      } catch (error) {
        if (cancelled) return;
        if (!error.isSessionExpired && !hasCachedActivities) {
          toast.error('获取活动数据失败');
        }
        
        if (!error.isSessionExpired && !hasCachedActivities) {
          setRecentActivities([
            {
              date: "今天 " + new Date().toLocaleTimeString('zh-CN', {hour: '2-digit', minute:'2-digit'}),
              title: "获取活动失败",
              description: "无法连接到服务器或获取数据",
              icon: AlertCircle
            }
          ]);
        }
      } finally {
        if (!cancelled) setActivityLoading(false);
      }
    };
    
    fetchRecentActivities();

    return () => {
      cancelled = true;
    };
  }, [overviewCacheKey]);
  
  // 根据活动类型确定图标
  const determineActivityIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'license':
        return Key;
      case 'license_delete': // 添加删除许可证类型
        return Trash2;
      case 'server':
        return Server;
      case 'system':
        return TrendingUp;
      case 'user':
        return User;
      default:
        return Calendar;
    }
  };

  // 格式化 ISO 时间字符串为东八区时间
  const formatToChineseTime = (isoString) => {
    if (!isoString) return '';
    
    try {
      // 创建日期对象
      const date = new Date(isoString);
      
      // 使用 toLocaleString 方法格式化为中国时间
      return date.toLocaleString('zh-CN', {
        timeZone: 'Asia/Shanghai',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
    } catch {
      return isoString; // 格式化失败时返回原始字符串
    }
  };

  // 获取系统信息
  useEffect(() => {
    let cancelled = false;
    const cachedOverview = readOverviewCache(overviewCacheKey);

    if (cachedOverview?.systemInfo) {
      setSystemInfo(cachedOverview.systemInfo);
    }

    if (isOverviewCacheFresh(cachedOverview?.systemInfoFetchedAt)) {
      return () => {
        cancelled = true;
      };
    }

    const fetchSystemInfo = async () => {
      try {
        // console.log('开始获取系统信息...');
        // 先尝试使用现代化API
        let response;
        try {
          response = await api.get(ENDPOINTS.SYSTEM_INFO_MODERN);
        } catch (modernError) {
          console.warn('现代化系统信息API失败，回退到传统API:', modernError);
          // 如果现代化API失败，回退到传统API
          response = await api.get(ENDPOINTS.SYSTEM_INFO);
        }
        
        const systemDataResponse = response.data;
        // console.log('系统信息响应:', systemDataResponse);
        
        // 使用新的格式化函数处理时间
        const lastUpdatedFormatted = formatToChineseTime(systemDataResponse.lastUpdated);

        if (cancelled) return;
        
        const nextSystemInfo = {
          version: systemDataResponse.version || 'v1.0.0',
          status: systemDataResponse.status || '正常',
          lastUpdated: lastUpdatedFormatted,
          isConnected: true, // 添加连接状态标记
          processCount: systemDataResponse.processCount || 0  // 添加进程数量
        };

        setSystemInfo(nextSystemInfo);
        updateOverviewCache(overviewCacheKey, {
          systemInfo: nextSystemInfo,
          systemInfoFetchedAt: Date.now(),
        });
      } catch {
        if (cancelled || cachedOverview?.systemInfo) return;

        // 保持默认系统信息，但也使用正确的时间格式
        setSystemInfo({
          version: 'v1.0.0',
          status: '无法连接',
          lastUpdated: formatToChineseTime(new Date().toISOString()),
          isConnected: false, // 添加连接状态标记
          processCount: 0  // 添加默认进程数量
        });
      }
    };
    
    fetchSystemInfo();

    return () => {
      cancelled = true;
    };
  }, [overviewCacheKey]);

  // 获取服务器详情信息
  const fetchServerInfo = async () => {
    try {
      setServerInfoLoading(true);
      setServerInfoError('');
      setSelectedProcessCategory(null);
      // console.log('开始获取服务器详情...');
      
      // 先尝试使用现代化API
      let response;
      try {
        response = await api.get(ENDPOINTS.SERVER_INFO_MODERN);
      } catch (modernError) {
        console.warn('现代化服务器信息API失败，回退到传统API:', modernError);
        // 如果现代化API失败，回退到传统API
        response = await api.get(ENDPOINTS.SERVER_INFO);
      }
      
      const serverInfoResponse = response.data;
      // console.log('服务器信息响应:', serverInfoResponse);
      
      setServerInfo(serverInfoResponse);
      setShowServerInfo(true);
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message || '未知错误';
      toast.error(`获取服务器详情失败: ${errorMessage}`);
      setServerInfo(null);
      setServerInfoError(errorMessage);
      setShowServerInfo(true);
    } finally {
      setServerInfoLoading(false);
    }
  };

  // 首页数据索引
  const stats = [
    { 
      title: "总许可证数量", 
      value: dashboardData.totalLicenses, 
      icon: Award, 
      caption: "累计签发",
      description: "全部许可证档案",
      unit: "份",
      isLoading
    },
    { 
      title: "当月生成许可证", 
      value: dashboardData.monthlyLicenses, 
      icon: Key, 
      caption: "本月新增",
      description: "当前自然月生成",
      unit: "份",
      isLoading
    },
    { 
      title: "运行程序数量", // 修改标题为运行程序数量
      value: systemInfo.processCount, // 使用系统信息中的进程数量
      icon: Server, 
      caption: "实时监测",
      description: "系统运行进程采样",
      unit: "个",
      isLoading
    }
  ];

  // 如果是管理员，添加用户数量卡片
  if (isAdmin) {
    stats.push({
      title: "系统用户", 
      value: dashboardData.userCount, 
      icon: Users, 
      caption: "当前用户",
      description: "已录入系统账号",
      unit: "位",
      isLoading
    });
  }

  // 工作流入口
  const features = [
    {
      title: "许可证生成器",
      description: "快速生成 Atlassian 产品和插件的许可证，支持各种配置选项。",
      icon: Settings2,
      buttonText: "生成许可证",
      onClick: () => navigate('/generate')
    },
    {
      title: "许可证记录",
      description: "查看并管理之前生成的所有许可证记录，方便查询和重用。",
      icon: FileText,
      buttonText: "查看记录",
      onClick: () => navigate('/licenses/conf')
    },
    {
      title: "个人中心",
      description: "管理您的个人资料、账号设置和密码安全。",
      icon: User,
      buttonText: "访问个人中心",
      onClick: () => navigate('/user/profile')
    }
  ];

  // 管理员特有的功能卡片
  if (isAdmin) {
    features.push({
      title: "系统配置",
      description: "管理应用访问地址、运行模式、安全密钥与授权引擎路径。",
      icon: Settings2,
      buttonText: "打开配置",
      onClick: () => navigate('/admin/configuration')
    });
  }

  const renderMetrics = () => {
    return stats.map((stat) => {
      return (
        <div
          key={stat.title}
          className={`desk-metric ${stat.title === '运行程序数量' ? 'desk-metric--interactive' : ''}`}
          role="listitem"
        >
          <div className="desk-metric__header">
            <div className="desk-metric__identity">
              <span className="desk-metric__label">{stat.title}</span>
            </div>
            <span className="desk-metric__status">{stat.caption}</span>
          </div>
          <div className="desk-metric__body">
            <span className="desk-metric__icon" aria-hidden="true"><stat.icon /></span>
            <div className="desk-metric__reading">
              {stat.isLoading ? (
                <span className="desk-metric__value desk-metric__value--loading">
                  <Loader2 className="animate-spin" aria-hidden="true" />加载中
                </span>
              ) : (
                <>
                  <strong className="desk-metric__value">{stat.value}</strong>
                  <span className="desk-metric__unit">{stat.unit}</span>
                </>
              )}
            </div>
          </div>
          <div className="desk-metric__footer">
            <span className="desk-metric__caption">{stat.description}</span>
            {stat.title === '运行程序数量' && !isLoading && (
              <button
                type="button"
                className="desk-metric__detail"
                onClick={fetchServerInfo}
                disabled={serverInfoLoading}
              >
                {serverInfoLoading ? <Loader2 className="animate-spin" /> : <EyeIcon />}
                <span>查看详情</span>
              </button>
            )}
          </div>
        </div>
      );
    });
  };

  // 服务器详情模态窗口
  const ServerInfoModal = () => {
    if (!showServerInfo) return null;
    
    const categoryTones = {
      system: 'slate',
      app: 'blue',
      java: 'orange',
      web: 'cyan',
      database: 'violet',
      other: 'amber',
      userApps: 'blue',
      services: 'cyan',
      development: 'violet',
      atlassian: 'indigo'
    };
    
    // 切换分类筛选
    const toggleCategory = (category) => {
      if (selectedProcessCategory === category) {
        setSelectedProcessCategory(null); // 如果已选中，取消选择
      } else {
        setSelectedProcessCategory(category); // 选择新分类
      }
    };
    
    // 获取筛选后的进程
    const filteredProcesses = !serverInfo?.processes
      ? []
      : selectedProcessCategory
        ? serverInfo.processes.filter((process) => process.category === selectedProcessCategory)
        : serverInfo.processes;

    const allProcesses = serverInfo?.processes || [];
    const totalProcesses = serverInfo?.summary?.totalProcesses ?? allProcesses.length;
    const cpuUsage = serverInfo?.summary?.cpuUsage ?? serverInfo?.summary?.systemLoad;
    const memoryUsage = serverInfo?.summary?.memoryUsage;
    const categoryEntries = Object.entries(serverInfo?.categories || {});
    const summaryMetrics = [
      {
        id: 'processes',
        label: '总进程数',
        value: formatSummaryMetric(totalProcesses),
        description: '当前主机已检测进程',
        icon: Layers,
        tone: 'indigo',
        progress: null,
      },
      {
        id: 'cpu',
        label: 'CPU 使用率',
        value: formatSummaryMetric(cpuUsage),
        description: '当前系统处理器负载',
        icon: Cpu,
        tone: 'orange',
        progress: getMetricPercentage(cpuUsage),
      },
      {
        id: 'memory',
        label: '内存使用率',
        value: formatSummaryMetric(memoryUsage),
        description: '当前系统内存占用',
        icon: MemoryStick,
        tone: 'violet',
        progress: getMetricPercentage(memoryUsage),
      },
    ];
    
    return createPortal(
      <div
        className="app-modal-layer server-process-dialog-layer"
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) setShowServerInfo(false);
        }}
      >
        <div
          className="server-process-dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="server-process-dialog-title"
          aria-describedby="server-process-dialog-description"
        >
          <header className="server-process-dialog__header">
            <div className="server-process-dialog__heading">
              <span className="server-process-dialog__heading-icon" aria-hidden="true"><Server /></span>
              <div>
                <small>SYSTEM OBSERVATORY</small>
                <h3 id="server-process-dialog-title" className="server-process-dialog__title">服务器运行程序详情</h3>
                <p id="server-process-dialog-description">查看当前主机进程、资源占用与程序分类快照。</p>
              </div>
            </div>
            <div className="server-process-dialog__header-actions">
              <span className="server-process-dialog__snapshot"><Activity aria-hidden="true" />当前采样</span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="server-process-dialog__close"
                onClick={() => setShowServerInfo(false)}
                aria-label="关闭服务器运行程序详情"
                title="关闭"
              >
                <X aria-hidden="true" />
              </Button>
            </div>
          </header>

          <div className="server-process-dialog__body">
            {serverInfo && (
              <div className="server-process-dialog__content">
                <section className="process-overview" aria-label="服务器资源概览">
                  {summaryMetrics.map((metric) => {
                    const MetricIcon = metric.icon;
                    return (
                      <article key={metric.id} className="process-overview__metric" data-tone={metric.tone}>
                        <div className="process-overview__metric-top">
                          <span className="process-overview__metric-icon" aria-hidden="true"><MetricIcon /></span>
                          <span className="process-overview__metric-label">{metric.label}</span>
                        </div>
                        <strong>{metric.value}</strong>
                        <p>{metric.description}</p>
                        {metric.progress !== null && (
                          <span className="process-overview__track" aria-hidden="true">
                            <i style={{ '--metric-progress': `${metric.progress}%` }} />
                          </span>
                        )}
                      </article>
                    );
                  })}
                </section>

                <section className="process-panel process-category-panel" aria-labelledby="process-category-title">
                  <div className="process-panel__header">
                    <div>
                      <span className="process-panel__eyebrow">FILTER BY TYPE</span>
                      <h4 id="process-category-title">进程分类</h4>
                      <p>点击分类即可筛选下方进程列表，再次点击可取消筛选。</p>
                    </div>
                    <span className="process-panel__count">{categoryEntries.length} 个分类</span>
                  </div>
                  <div className="process-category-grid">
                    {categoryEntries.map(([key, category]) => {
                      const categoryCount = category.count ?? allProcesses.filter((process) => process.category === key).length;
                      const isSelected = selectedProcessCategory === key;
                      return (
                        <button
                          key={key}
                          type="button"
                          className="process-category"
                          data-tone={categoryTones[key] || 'slate'}
                          data-selected={isSelected || undefined}
                          aria-pressed={isSelected}
                          onClick={() => toggleCategory(key)}
                        >
                          <span className="process-category__count">{categoryCount}</span>
                          <span className="process-category__name">{category.name || key}</span>
                          <CheckCircle2 className="process-category__selected" aria-hidden="true" />
                        </button>
                      );
                    })}
                  </div>
                </section>

                <section className="process-panel process-table-panel" aria-labelledby="process-list-title">
                  <div className="process-panel__header process-table-panel__header">
                    <div>
                      <span className="process-panel__eyebrow">PROCESS INVENTORY</span>
                      <h4 id="process-list-title">
                        {selectedProcessCategory ? serverInfo.categories?.[selectedProcessCategory]?.name || selectedProcessCategory : '所有进程'}
                      </h4>
                      <p>按进程查看 PID、内存、CPU 与识别分类。</p>
                    </div>
                    <div className="process-table-panel__tools">
                      <span className="process-panel__count">{filteredProcesses.length} 条记录</span>
                      {selectedProcessCategory && (
                        <Button type="button" variant="ghost" size="sm" onClick={() => setSelectedProcessCategory(null)}>
                          <X aria-hidden="true" />清除筛选
                        </Button>
                      )}
                    </div>
                  </div>

                  {filteredProcesses.length > 0 ? (
                    <div className="process-table-wrap">
                      <table className="process-table">
                        <thead>
                          <tr>
                            <th>进程名称</th>
                            <th>PID</th>
                            <th>内存</th>
                            <th>CPU</th>
                            <th>类别</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredProcesses.map((process, index) => (
                            <tr key={`${process.pid ?? process.name}-${index}`}>
                              <td>
                                <div className="process-table__name">{process.name || '未命名进程'}</div>
                                {process.description && (
                                  <div className="process-table__description" title={process.description}>{process.description}</div>
                                )}
                              </td>
                              <td><code>{process.pid ?? '-'}</code></td>
                              <td><span className="process-table__resource">{formatProcessMemory(process)}</span></td>
                              <td><span className="process-table__resource">{formatProcessCPU(process)}</span></td>
                              <td>
                                <span className="process-table__category" data-tone={categoryTones[process.category] || 'slate'}>
                                  {serverInfo.categories?.[process.category]?.name || process.category || '未分类'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="server-process-empty">
                      <span aria-hidden="true"><Layers /></span>
                      <strong>{selectedProcessCategory ? '当前分类没有匹配进程' : '没有检测到进程信息'}</strong>
                      <p>{selectedProcessCategory ? '可以清除筛选查看全部进程。' : '服务器暂未返回可展示的进程记录。'}</p>
                      {selectedProcessCategory && (
                        <Button type="button" variant="outline" size="sm" onClick={() => setSelectedProcessCategory(null)}>查看全部进程</Button>
                      )}
                    </div>
                  )}
                </section>
              </div>
            )}

            {!serverInfo && (
              <div className="server-process-empty server-process-empty--error">
                <span aria-hidden="true"><AlertCircle /></span>
                <strong>{serverInfoError ? '服务器信息加载失败' : '暂无服务器数据'}</strong>
                <p>{serverInfoError || '服务器暂未返回可展示的运行程序信息。'}</p>
                {serverInfoError && (
                  <Button type="button" variant="outline" size="sm" onClick={fetchServerInfo} disabled={serverInfoLoading}>
                    {serverInfoLoading ? <Loader2 className="animate-spin" aria-hidden="true" /> : <Activity aria-hidden="true" />}
                    {serverInfoLoading ? '重新加载中' : '重新加载'}
                  </Button>
                )}
              </div>
            )}
          </div>

          <footer className="server-process-dialog__footer">
            <span>
              {serverInfo
                ? <>当前显示 <strong>{filteredProcesses.length}</strong> / {allProcesses.length} 条进程</>
                : '未加载到服务器进程数据'}
            </span>
            <Button
              type="button"
              variant="outline"
              className="server-process-dialog__footer-close"
              onClick={() => setShowServerInfo(false)}
            >
              <X aria-hidden="true" />
              关闭
            </Button>
          </footer>
        </div>
      </div>,
      document.body,
    );
  };

  return (
    <div className="h-full overflow-y-auto">
      <main className="license-console" aria-labelledby="license-console-title">
        <section className="issuance-desk">
          <header className="issuance-desk__masthead">
            <div className="issuance-desk__identity">
              <span className="issuance-desk__seal" aria-hidden="true">ALM</span>
              <div>
                <strong>LICENSE OPERATIONS</strong>
                <span>ATLASSIAN AUTHORIZATION DESK</span>
              </div>
            </div>
            <div className="issuance-desk__session">
              <time dateTime={new Date().toISOString()}>
                {new Date().toLocaleDateString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit', weekday: 'short' })}
              </time>
              <span className={systemInfo.isConnected ? 'is-online' : 'is-offline'}>
                <i aria-hidden="true" />{systemInfo.isConnected ? '服务在线' : '连接异常'}
              </span>
            </div>
          </header>

          <div className="issuance-desk__body">
            <section className="issuance-brief">
              <div className="issuance-brief__copy">
                <h1 id="license-console-title">授权签发<br /><span>控制台</span></h1>
                <p className="issuance-brief__intro">
                  欢迎回来，{user?.username || '用户'}。从创建授权到查阅档案，关键流程已在当前工作台就绪。
                </p>
                <div className="issuance-brief__actions">
                  <Button size="lg" onClick={() => navigate('/generate')}>
                    <Key data-icon="inline-start" />创建新许可证
                  </Button>
                  <Button size="lg" variant="outline" onClick={() => navigate('/licenses/conf')}>
                    打开许可证档案<ArrowRight data-icon="inline-end" />
                  </Button>
                </div>
              </div>

              <div className="license-counter" aria-label="许可证总量">
                <span className="license-counter__caption">TOTAL ISSUED</span>
                {isLoading ? (
                  <Loader2 className="license-counter__loader animate-spin" aria-label="正在加载许可证总量" />
                ) : (
                  <strong>{String(dashboardData.totalLicenses).padStart(3, '0')}</strong>
                )}
                <span className="license-counter__unit">LICENSE FILES</span>
                <i aria-hidden="true">AUTH / VERIFIED</i>
              </div>
            </section>

            <nav className="workflow-index" aria-label="常用工作流">
              <div className="workflow-index__heading">
                <div>
                  <span>WORKFLOW INDEX</span>
                  <h2>工作流索引</h2>
                </div>
                <small>{String(features.length).padStart(2, '0')} ROUTES</small>
              </div>
              <div className="workflow-index__list">
                {features.map((feature, index) => (
                  <FeatureCard
                    key={feature.title}
                    index={index}
                    title={feature.title}
                    description={feature.description}
                    icon={feature.icon}
                    buttonText={feature.buttonText}
                    onClick={feature.onClick}
                  />
                ))}
              </div>
            </nav>
          </div>

          <div className="desk-metrics" data-count={stats.length} role="list" aria-label="运行数据">
            {renderMetrics()}
          </div>
        </section>

        <section className="operations-ledger">
          <article className="activity-ledger">
            <header className="ledger-heading ledger-heading--activity">
              <div className="ledger-heading__identity">
                <span className="ledger-heading__icon" aria-hidden="true"><Activity /></span>
                <div>
                  <span>ACTIVITY LEDGER / 近期记录</span>
                  <h2>操作账本</h2>
                </div>
              </div>
              <div className="ledger-heading__summary" aria-live="polite">
                <strong>{activityLoading ? '--' : String(recentActivities.length).padStart(2, '0')}</strong>
                <span>条近期记录</span>
              </div>
            </header>
            <div className="activity-ledger__body custom-scrollbar">
              <div className="activity-ledger__columns" aria-hidden="true">
                <span>发生时间</span>
                <span>操作详情</span>
              </div>
              {activityLoading ? (
                <>
                  <ActivitySkeleton />
                  <ActivitySkeleton />
                  <ActivitySkeleton />
                  <ActivitySkeleton />
                  <ActivitySkeleton />
                </>
              ) : recentActivities.length > 0 ? (
                visibleActivities.map((activity, index) => (
                  <ActivityItem
                    key={`${activity.date}-${activityPageStart + index}`}
                    date={activity.date}
                    title={activity.title}
                    description={activity.description}
                    icon={activity.icon}
                  />
                ))
              ) : (
                <Empty><EmptyIcon><Calendar /></EmptyIcon><EmptyTitle>暂无活动记录</EmptyTitle><EmptyDescription>系统产生的新操作会显示在这里。</EmptyDescription></Empty>
              )}
            </div>
            <footer className="activity-ledger__footer">
              <span>
                <FileText aria-hidden="true" />
                {activityLoading
                  ? '正在同步操作记录'
                  : recentActivities.length > 0
                    ? `第 ${activityPageStart + 1}–${activityPageEnd} 条，共 ${recentActivities.length} 条`
                    : '暂无操作记录'}
              </span>
              {recentActivities.length > ACTIVITY_PAGE_SIZE ? (
                <nav className="activity-ledger__pagination" aria-label="操作记录分页">
                  <button
                    type="button"
                    className="activity-ledger__page-button"
                    onClick={() => setActivityPage((page) => Math.max(1, page - 1))}
                    disabled={activityPage === 1}
                    aria-label="上一页"
                    title="上一页"
                  >
                    <ChevronLeft aria-hidden="true" />
                  </button>
                  <span className="activity-ledger__page-status" aria-live="polite">
                    <strong>{activityPage}</strong>
                    <i>/</i>
                    <span>{activityPageCount}</span>
                  </span>
                  <button
                    type="button"
                    className="activity-ledger__page-button"
                    onClick={() => setActivityPage((page) => Math.min(activityPageCount, page + 1))}
                    disabled={activityPage === activityPageCount}
                    aria-label="下一页"
                    title="下一页"
                  >
                    <ChevronRight aria-hidden="true" />
                  </button>
                </nav>
              ) : (
                <small>按操作时间倒序排列</small>
              )}
            </footer>
          </article>

          <aside className="system-telemetry" aria-labelledby="system-telemetry-title">
            <header className="ledger-heading ledger-heading--inverse">
              <div>
                <span>SYSTEM TELEMETRY / 系统遥测</span>
                <h2 id="system-telemetry-title">运行状态</h2>
              </div>
              <span
                className={`telemetry-heading__status ${systemInfo.isConnected ? 'is-online' : 'is-offline'}`}
                aria-label={systemInfo.isConnected ? '系统连接正常' : '系统连接异常'}
              >
                {systemInfo.isConnected ? <CheckCircle2 aria-hidden="true" /> : <XCircle aria-hidden="true" />}
              </span>
            </header>

            <div className={`telemetry-verdict ${systemInfo.isConnected ? 'is-online' : 'is-offline'}`}>
              <div className="telemetry-verdict__copy">
                <span>CONNECTION</span>
                <div className="telemetry-verdict__state">
                  <strong>{systemInfo.isConnected ? 'ONLINE' : 'OFFLINE'}</strong>
                  <small>{systemInfo.status}</small>
                </div>
              </div>
              <span className="telemetry-verdict__signal" aria-hidden="true">
                <Server />
              </span>
            </div>

            <dl className="telemetry-table">
              <div><dt>系统版本</dt><dd>{systemInfo.version}</dd></div>
              <div><dt>运行进程</dt><dd>{systemInfo.processCount}</dd></div>
              <div><dt>Agent Runtime</dt><dd>JAVA 11+</dd></div>
              <div><dt>最后同步</dt><dd>{systemInfo.lastUpdated}</dd></div>
            </dl>

            <Button
              variant="outline"
              className="telemetry-detail-button"
              onClick={fetchServerInfo}
              disabled={serverInfoLoading}
            >
              {serverInfoLoading ? <Loader2 className="animate-spin" data-icon="inline-start" /> : <EyeIcon data-icon="inline-start" />}
              查看服务器进程详情
            </Button>

            <div className="telemetry-help">
              <HelpCircle aria-hidden="true" />
              <p><strong>需要协助？</strong><span>请联系系统管理员获取运行支持与授权配置帮助。</span></p>
            </div>
          </aside>
        </section>

      {/* 服务器详情模态框 */}
      <ServerInfoModal />
      </main>
    </div>
  );
};

export default Home;
